/*
 * Exowide Atelier – product form
 *
 * Rules this file guarantees:
 *  - The variant ID sent to Shopify is always the ID of the variant whose options are
 *    currently selected. If no such variant exists, nothing is sent.
 *  - Every submit path (button, Enter key, apps calling form.submit()) goes through
 *    the same validated Ajax request to /cart/add.js – never a raw POST to /cart/add.
 *  - Stock logic follows Shopify: variant.available, inventory tracking and the
 *    "continue selling when out of stock" policy. "Only 0 left" cannot be produced.
 */
(function (global) {
  'use strict';

  /* ---------- Pure logic (no DOM) – also exported for automated tests ---------- */

  function findVariant(variants, selected) {
    if (!Array.isArray(variants) || !Array.isArray(selected)) return null;
    if (selected.some(function (v) { return v === null || v === undefined; })) return null;
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      if (opts.length !== selected.length) continue;
      var match = true;
      for (var j = 0; j < opts.length; j++) {
        if (opts[j] !== selected[j]) { match = false; break; }
      }
      if (match) return variants[i];
    }
    return null;
  }

  function isTracked(v) {
    return !!(v && v.inventory_management);
  }

  function deniesOversell(v) {
    return isTracked(v) && v.inventory_policy !== 'continue';
  }

  /**
   * Returns { key, count, canBuy }
   * key: unavailable | soldout | backorder | low | in_count | in
   */
  function stockState(v, cfg) {
    cfg = cfg || {};
    var threshold = Number(cfg.lowThreshold) > 0 ? Number(cfg.lowThreshold) : 10;
    if (!v) return { key: 'unavailable', count: null, canBuy: false };
    if (!v.available) return { key: 'soldout', count: null, canBuy: false };
    var qty = Number(v.inventory_quantity);
    if (isTracked(v)) {
      if (v.inventory_policy === 'continue') {
        if (!(qty > 0)) return { key: 'backorder', count: null, canBuy: true };
      } else if (qty > 0) {
        if (qty <= threshold) return { key: 'low', count: qty, canBuy: true };
        if (cfg.showCount) return { key: 'in_count', count: qty, canBuy: true };
      }
    }
    // Shopify says the variant is available: trust it (never print a count of 0).
    return { key: 'in', count: null, canBuy: true };
  }

  /** Highest quantity allowed in one add, or null when there is no limit. */
  function maxQuantity(v, cfg) {
    cfg = cfg || {};
    var limits = [];
    if (deniesOversell(v)) {
      var qty = Number(v.inventory_quantity);
      if (qty > 0) limits.push(qty);
    }
    if (v && v.quantity_rule && Number(v.quantity_rule.max) > 0) limits.push(Number(v.quantity_rule.max));
    if (Number(cfg.orderLimit) > 0) limits.push(Number(cfg.orderLimit));
    return limits.length ? Math.min.apply(null, limits) : null;
  }

  /** Whole number >= 1 parsed from user input, otherwise NaN. */
  function parseQuantity(raw) {
    var s = String(raw === undefined || raw === null ? '' : raw).trim();
    if (!/^\d+$/.test(s)) return NaN;
    var n = parseInt(s, 10);
    return n >= 1 && Number.isSafeInteger(n) ? n : NaN;
  }

  /** Validates a purchase. Returns { ok, reason, max } */
  function validatePurchase(v, quantity, cfg) {
    var state = stockState(v, cfg);
    if (!v) return { ok: false, reason: 'unavailable' };
    if (!state.canBuy) return { ok: false, reason: 'soldout' };
    var n = parseQuantity(quantity);
    if (isNaN(n)) return { ok: false, reason: 'quantity_invalid' };
    var max = maxQuantity(v, cfg);
    if (max !== null && n > max) return { ok: false, reason: 'quantity_max', max: max };
    return { ok: true, quantity: n, id: v.id };
  }

  /** The exact payload sent to /cart/add.js */
  function buildCartPayload(v, quantity) {
    return { id: Number(v.id), quantity: quantity };
  }

  /** For each option value: 'ok' | 'soldout' | 'missing', given the other selected values. */
  function optionStates(variants, selected, cfg) {
    return function (index, value) {
      var trial = selected.slice();
      trial[index] = value;
      var v = findVariant(variants, trial);
      if (!v) return 'missing';
      return stockState(v, cfg).canBuy ? 'ok' : 'soldout';
    };
  }

  function normaliseKey(text) {
    return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /** Colours (from the list) whose normalised name appears in the media key. */
  function mediaColours(mediaKey, colours) {
    var key = normaliseKey(mediaKey);
    return colours.filter(function (c) {
      var n = normaliseKey(c);
      return n.length > 2 && key.indexOf(n) !== -1;
    });
  }

  var Logic = {
    normaliseKey: normaliseKey,
    mediaColours: mediaColours,
    findVariant: findVariant,
    stockState: stockState,
    maxQuantity: maxQuantity,
    parseQuantity: parseQuantity,
    validatePurchase: validatePurchase,
    buildCartPayload: buildCartPayload,
    optionStates: optionStates
  };
  global.ExowideProductLogic = Logic;
  if (typeof module !== 'undefined' && module.exports) module.exports = Logic;
  if (typeof document === 'undefined') return;

  /* ---------- DOM ---------- */

  function fill(template, key, value) {
    return String(template || '').split('[' + key + ']').join(String(value));
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function ProductForm(root) {
    this.root = root;
    this.form = root.querySelector('form[data-product-form]');
    this.variants = readJSON(root, '[data-variant-json]');
    this.t = readJSON(root, '[data-product-strings]') || {};
    if (!this.form || !Array.isArray(this.variants) || !this.variants.length) return;

    this.cfg = {
      lowThreshold: Number(root.getAttribute('data-low-threshold')) || 10,
      showCount: root.getAttribute('data-show-count') === 'true',
      orderLimit: Number(root.getAttribute('data-order-limit')) || 0
    };
    this.afterAdd = root.getAttribute('data-after-add') || 'message';
    this.idInput = this.form.querySelector('[data-variant-id]');
    this.groups = Array.prototype.slice.call(this.form.querySelectorAll('[data-option-group]'));
    this.qtyInput = this.form.querySelector('[data-qty-input]');
    this.button = this.form.querySelector('[data-add-button]');
    this.buttonLabel = this.form.querySelector('[data-add-label]');
    this.message = this.form.querySelector('[data-form-message]');
    this.stock = root.querySelector('[data-stock]');
    this.price = root.querySelector('[data-price]');
    this.compare = root.querySelector('[data-compare]');
    this.busy = false;

    // Switch from the no-JS <select name="id"> to the enhanced pickers.
    var noJs = this.form.querySelector('[data-no-js-select]');
    if (noJs) {
      var wrap = noJs.closest('.no-js-variant-select');
      (wrap || noJs).parentNode.removeChild(wrap || noJs);
    }
    if (this.idInput) this.idInput.disabled = false;
    root.classList.add('is-enhanced');

    this.bind();
    this.applyInitialSelection();
    this.update({ initial: true });
  }

  ProductForm.prototype.selectedValues = function () {
    return this.groups.map(function (group) {
      var checked = group.querySelector('[data-option-input]:checked');
      return checked ? checked.value : null;
    });
  };

  ProductForm.prototype.currentVariant = function () {
    if (!this.groups.length) return this.variants[0]; // product without options
    return findVariant(this.variants, this.selectedValues());
  };

  ProductForm.prototype.applyInitialSelection = function () {
    var params = new URLSearchParams(global.location.search);
    var fromUrl = params.get('variant');
    var initial = null;
    if (fromUrl) initial = this.variants.filter(function (v) { return String(v.id) === fromUrl; })[0] || null;
    if (!initial && this.idInput) {
      var idValue = this.idInput.value;
      initial = this.variants.filter(function (v) { return String(v.id) === idValue; })[0] || null;
    }
    if (!initial) return;
    this.groups.forEach(function (group, i) {
      var target = initial.options[i];
      Array.prototype.forEach.call(group.querySelectorAll('[data-option-input]'), function (input) {
        input.checked = input.value === target;
      });
    });
  };

  ProductForm.prototype.bind = function () {
    var self = this;
    this.form.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute('data-option-input')) self.update({ fromUser: true });
    });
    if (this.qtyInput) {
      this.qtyInput.addEventListener('input', function () { self.clearMessage(); });
      this.qtyInput.addEventListener('blur', function () { self.normaliseQuantity(); });
    }
    Array.prototype.forEach.call(this.form.querySelectorAll('[data-qty-step]'), function (btn) {
      btn.addEventListener('click', function () {
        var step = Number(btn.getAttribute('data-qty-step'));
        var n = parseQuantity(self.qtyInput.value);
        if (isNaN(n)) n = 1;
        var max = maxQuantity(self.currentVariant(), self.cfg);
        n = Math.max(1, n + step);
        if (max !== null) n = Math.min(n, max);
        self.qtyInput.value = String(n);
        self.clearMessage();
      });
    });

    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self.submit();
    });
    // Apps sometimes call form.submit(), which skips submit listeners and would
    // POST the raw form to /cart/add. Route it through the validated path instead.
    try {
      this.form.submit = function () { self.submit(); };
    } catch (err) { /* read-only in some engines – submit listener still applies */ }
  };

  // Never change what the customer typed behind their back – explain instead.
  ProductForm.prototype.normaliseQuantity = function () {
    if (!this.qtyInput || this.busy) return;
    var n = parseQuantity(this.qtyInput.value);
    if (isNaN(n)) { this.showMessage('error', this.t.quantity_invalid); return; }
    var max = maxQuantity(this.currentVariant(), this.cfg);
    if (max !== null && n > max) this.showMessage('error', fill(this.t.quantity_max, 'max', max));
  };

  ProductForm.prototype.update = function (opts) {
    opts = opts || {};
    var self = this;
    var v = this.currentVariant();
    var state = stockState(v, this.cfg);
    var t = this.t;

    // 1. Variant ID – always the matched variant, never a stale one.
    if (this.idInput) this.idInput.value = v ? String(v.id) : '';

    // 2. Price – hidden while the selected option can't be bought (sold out / missing),
    //    so an unavailable size never shows a confusing price. Kept in layout (no jump).
    var hidePrice = this.root.getAttribute('data-hide-soldout-price') !== 'false' && !state.canBuy;
    if (this.price) {
      this.price.innerHTML = v ? v.price_formatted : (this.root.getAttribute('data-from-price') || '');
      this.price.style.visibility = hidePrice ? 'hidden' : '';
      this.price.setAttribute('aria-hidden', hidePrice ? 'true' : 'false');
    }
    if (this.compare) {
      var discounted = !hidePrice && !!(v && v.compare_at_price > v.price);
      this.compare.hidden = !discounted;
      this.compare.innerHTML = discounted ? v.compare_formatted : '';
    }

    // 3. Stock message
    if (this.stock) {
      var text;
      switch (state.key) {
        case 'unavailable': text = t.unavailable; break;
        case 'soldout': text = t.sold_out; break;
        case 'backorder': text = t.backorder; break;
        case 'low': text = fill(t.low_stock, 'count', state.count); break;
        case 'in_count': text = fill(t.in_stock_count, 'count', state.count); break;
        default: text = t.in_stock;
      }
      this.stock.setAttribute('data-state', state.key);
      this.stock.innerHTML = '<span class="stock-dot" aria-hidden="true"></span>';
      this.stock.appendChild(document.createTextNode(text || ''));
    }

    // 4. Quantity limits
    var max = maxQuantity(v, this.cfg);
    if (this.qtyInput) {
      if (max !== null) {
        this.qtyInput.max = String(max);
        var n = parseQuantity(this.qtyInput.value);
        if (!isNaN(n) && n > max) this.qtyInput.value = String(max);
      } else {
        this.qtyInput.removeAttribute('max');
      }
    }

    // 5. Button
    this.renderButton(state);

    // 6. Option states (mark, never hide or auto-switch)
    var selected = this.selectedValues();
    var stateOf = optionStates(this.variants, selected, this.cfg);
    this.groups.forEach(function (group, index) {
      var current = group.querySelector('[data-option-current]');
      if (current) current.textContent = selected[index] || '';
      Array.prototype.forEach.call(group.querySelectorAll('[data-option-input]'), function (input) {
        var s = stateOf(index, input.value);
        var label = input.nextElementSibling;
        if (!label) return;
        label.setAttribute('data-state', s);
        var note = label.querySelector('[data-option-state]');
        if (note) note.textContent = s === 'ok' ? '' : (s === 'soldout' ? t.option_sold_out : t.option_unavailable);
      });
    });

    // 7. Media: show the selected colour's images, variant image first
    if (global.ExowideGallery) {
      var colourGroup = this.root.querySelector('.option-group--color');
      var colourIndex = colourGroup ? this.groups.indexOf(colourGroup) : -1;
      if (colourIndex !== -1) {
        global.ExowideGallery.filter(this.root, selected[colourIndex], v ? v.featured_media_id : null);
      }
      if (v && v.featured_media_id) {
        global.ExowideGallery.show(this.root, v.featured_media_id, { instant: !!opts.initial || colourIndex !== -1 });
      }
    }

    // 8. Sticky bar mirrors the form
    var bar = this.root.querySelector('[data-sticky-atc]');
    if (bar) {
      var bPrice = bar.querySelector('[data-sticky-price]');
      var bVariant = bar.querySelector('[data-sticky-variant]');
      var bButton = bar.querySelector('[data-sticky-add]');
      if (bPrice && this.price) {
        bPrice.innerHTML = this.price.innerHTML;
        bPrice.style.visibility = hidePrice ? 'hidden' : '';
      }
      if (bVariant) bVariant.textContent = v ? v.title : '';
      if (bButton) {
        bButton.disabled = !state.canBuy;
        bButton.textContent = state.canBuy ? t.add_to_cart : (state.key === 'unavailable' ? t.unavailable : t.sold_out);
      }
    }
    if (v && opts.fromUser && global.history && global.history.replaceState) {
      var url = new URL(global.location.href);
      url.searchParams.set('variant', String(v.id));
      global.history.replaceState(global.history.state, '', url.toString());
    }
    if (opts.fromUser) this.clearMessage();
  };

  ProductForm.prototype.renderButton = function (state) {
    if (!this.button) return;
    state = state || stockState(this.currentVariant(), this.cfg);
    this.button.disabled = this.busy || !state.canBuy;
    this.button.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    var label = this.busy ? this.t.adding : (state.canBuy ? this.t.add_to_cart : (state.key === 'unavailable' ? this.t.unavailable : this.t.sold_out));
    if (this.buttonLabel) this.buttonLabel.textContent = label || '';
  };

  ProductForm.prototype.clearMessage = function () {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.className = 'form-message';
    this.message.textContent = '';
  };

  ProductForm.prototype.showMessage = function (kind, text, withLinks) {
    if (!this.message) return;
    this.message.hidden = false;
    this.message.className = 'form-message form-message--' + kind;
    this.message.textContent = text;
    if (withLinks) {
      var links = document.createElement('span');
      links.className = 'form-message-links';
      var cart = document.createElement('a');
      cart.href = this.t.cart_url || '/cart';
      cart.textContent = this.t.view_cart;
      var checkout = document.createElement('a');
      checkout.href = (this.t.root_url || '/').replace(/\/?$/, '/') + 'checkout';
      checkout.className = 'button button-dark button-small';
      checkout.textContent = this.t.checkout;
      links.appendChild(cart);
      links.appendChild(checkout);
      this.message.appendChild(links);
    }
  };

  ProductForm.prototype.submit = function () {
    var self = this;
    if (this.busy) return;
    var v = this.currentVariant();
    var check = validatePurchase(v, this.qtyInput ? this.qtyInput.value : 1, this.cfg);
    if (!check.ok) {
      var msg = check.reason === 'quantity_max' ? fill(this.t.quantity_max, 'max', check.max) : this.t[check.reason === 'soldout' ? 'sold_out' : check.reason];
      this.showMessage('error', msg || this.t.error_generic);
      if (check.reason === 'quantity_invalid' && this.qtyInput) this.qtyInput.focus();
      return;
    }

    // Collect app line-item properties / selling plan from the form, but force the
    // validated variant id + quantity, and drop our option radios.
    var body = new FormData(this.form);
    Array.from(body.keys()).forEach(function (key) {
      if (key.indexOf('option-') === 0) body.delete(key);
    });
    var payload = buildCartPayload(v, check.quantity);
    body.set('id', String(payload.id));
    body.set('quantity', String(payload.quantity));

    this.busy = true;
    this.renderButton();
    this.clearMessage();

    var addUrl = (this.t.cart_add_url || '/cart/add') + '.js';
    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (data) {
          if (!res.ok) {
            var err = new Error(data.description || data.message || '');
            err.status = res.status;
            throw err;
          }
          return data;
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('exowide:cart-updated'));
        if (self.afterAdd === 'cart') {
          global.location.assign(self.t.cart_url || '/cart');
          return;
        }
        self.showMessage('success', self.t.added, true);
      })
      .catch(function (err) {
        // Shopify returns customer-readable descriptions (e.g. stock limits) on 422.
        var text = err && err.status === 422 && err.message ? err.message : self.t.error_generic;
        self.showMessage('error', text);
      })
      .then(function () {
        self.busy = false;
        self.renderButton();
      });
  };

  /* ---------- Gallery ---------- */

  var reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var Gallery = {
    show: function (root, mediaId, opts) {
      var track = root.querySelector('[data-gallery-track]');
      var slide = root.querySelector('[data-media-id="' + mediaId + '"]');
      if (!track || !slide) return;
      if (track.scrollWidth <= track.clientWidth + 2) { Gallery.mark(root, mediaId); return; } // desktop grid: nothing to scroll
      var ordered = Gallery.lead(root);
      var first = ordered[0];
      track.scrollTo({ left: slide.offsetLeft - (first ? first.offsetLeft : track.offsetLeft), behavior: opts && opts.instant || reduceMotion ? 'auto' : 'smooth' });
      Gallery.mark(root, mediaId);
    },
    colours: function (root) {
      return Array.prototype.map.call(root.querySelectorAll('.option-group--color [data-option-input]'), function (i) { return i.value; });
    },
    filter: function (root, colour, leadId) {
      var gallery = root.querySelector('[data-gallery]');
      if (!gallery || gallery.getAttribute('data-group-media') !== 'true' || !colour) return;
      var colours = Gallery.colours(root);
      var slides = root.querySelectorAll('[data-media-key]');
      var visible = 0;
      // Only group when at least one image is tied to a colour – otherwise show everything.
      var anyMatched = Array.prototype.some.call(slides, function (sl) { return mediaColours(sl.getAttribute('data-media-key'), colours).length; });
      Array.prototype.forEach.call(slides, function (slide) {
        var id = slide.getAttribute('data-media-id');
        var own = mediaColours(slide.getAttribute('data-media-key'), colours);
        var show = !anyMatched || !own.length || own.indexOf(colour) !== -1 || String(id) === String(leadId);
        slide.hidden = !show;
        slide.style.order = String(id) === String(leadId) ? '-1' : '';
        var thumb = root.querySelector('[data-thumb-for="' + id + '"]');
        if (thumb) { thumb.hidden = !show; thumb.style.order = slide.style.order; }
        if (show) visible++;
      });
      Gallery.lead(root);
      Gallery.count(root, 1, visible);
    },
    lead: function (root) {
      var slides = Array.prototype.filter.call(root.querySelectorAll('.gallery-slide'), function (s) { return !s.hidden; });
      slides.sort(function (a, b) { return (Number(a.style.order) || 0) - (Number(b.style.order) || 0); });
      Array.prototype.forEach.call(root.querySelectorAll('.gallery-slide'), function (s) { s.classList.remove('is-lead'); });
      if (slides[0]) slides[0].classList.add('is-lead');
      return slides;
    },
    count: function (root, current, total) {
      var el = root.querySelector('[data-gallery-counter]');
      if (!el) return;
      if (total === undefined) total = Gallery.lead(root).length;
      el.textContent = current + ' / ' + total;
      el.hidden = total < 2;
    },
    mark: function (root, mediaId) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-thumb-for]'), function (thumb) {
        var active = thumb.getAttribute('data-thumb-for') === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
      });
    },
    init: function (root) {
      var track = root.querySelector('[data-gallery-track]');
      if (!track) return;
      Array.prototype.forEach.call(root.querySelectorAll('[data-thumb-for]'), function (thumb) {
        thumb.addEventListener('click', function () { Gallery.show(root, thumb.getAttribute('data-thumb-for')); });
      });
      if ('IntersectionObserver' in global) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              Gallery.mark(root, entry.target.getAttribute('data-media-id'));
              var ordered = Gallery.lead(root);
              Gallery.count(root, ordered.indexOf(entry.target) + 1, ordered.length);
            }
          });
        }, { root: track, threshold: 0.6 });
        Array.prototype.forEach.call(track.querySelectorAll('[data-media-id]'), function (s) { io.observe(s); });
      }
      var dialog = root.querySelector('[data-zoom-dialog]');
      if (dialog && typeof dialog.showModal === 'function') {
        var img = dialog.querySelector('[data-zoom-image]');
        Array.prototype.forEach.call(root.querySelectorAll('[data-zoom-src]'), function (btn) {
          btn.addEventListener('click', function () {
            img.src = btn.getAttribute('data-zoom-src');
            img.alt = btn.getAttribute('data-zoom-alt') || '';
            dialog.showModal();
          });
        });
        dialog.querySelector('[data-zoom-close]').addEventListener('click', function () { dialog.close(); });
        dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
      }
    }
  };
  global.ExowideGallery = Gallery;

  function initStickyBar(root) {
    var bar = root.querySelector('[data-sticky-atc]');
    var main = root.querySelector('[data-add-button]');
    var form = root.__exowideForm;
    if (!bar || !main || !form || !form.form || !('IntersectionObserver' in global)) return;
    var button = bar.querySelector('[data-sticky-add]');
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (button) button.tabIndex = show ? 0 : -1;
    });
    io.observe(main);
    if (button) button.addEventListener('click', function () {
      // Bring the selected options and the result message into view, then add.
      form.form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      form.submit();
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-exowide-product]'), function (root) {
      if (root.__exowide) return;
      root.__exowide = true;
      try { Gallery.init(root); } catch (e) { if (global.console) console.error('[Exowide] gallery', e); }
      try { root.__exowideForm = new ProductForm(root); } catch (e) {
        if (global.console) console.error('[Exowide] product form', e);
      }
      try { initStickyBar(root); } catch (e) { if (global.console) console.error('[Exowide] sticky bar', e); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})(typeof window !== 'undefined' ? window : globalThis);
